"use client";

import Link from "next/link";
import { useEffect, useState } from "react";
import { Button, NutcakeLongLogo } from "@drpcrd/shared/ui";
import { cn } from "@drpcrd/utils";

export const OFFER_FORM_ID = "offer-form";

type Props = {
  /** Label of the mobile button that scrolls to the form. */
  ctaLabel?: string;
  /** Label of the desktop button that links to the contact page. */
  desktopCtaLabel?: string;
};

/**
 * Sticky top navigation for offer pages.
 * Below lg it shows a single call-to-action button that scrolls to the form
 * section; from lg up the form sidebar is already in view, so the standard
 * sign-in links are shown instead.
 */
export const OfferPageNavbar = ({
  ctaLabel = "Get offer",
  desktopCtaLabel = "Start now",
}: Props) => {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 80);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const onGetOfferClick = () => {
    document.getElementById(OFFER_FORM_ID)?.scrollIntoView({
      behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches
        ? "auto"
        : "smooth",
      block: "start",
    });
  };

  return (
    <header
      aria-label="Site navigation"
      className={cn(
        "fixed top-0 z-50 flex w-full items-center justify-between px-2 py-2 transition-all duration-300 motion-reduce:transition-none",
        scrolled ? "bg-white" : "bg-transparent",
      )}
    >
      <Link
        href="/"
        aria-label="Nutcake home"
        className="flex shrink-0 items-center"
      >
        <NutcakeLongLogo className="h-4 w-auto shrink-0 sm:h-5.25" />
      </Link>

      <Button variant="filled" className="lg:hidden" onClick={onGetOfferClick}>
        {ctaLabel}
      </Button>

      <div className="hidden shrink-0 items-center gap-3 lg:flex">
        <Button
          as={Link}
          href="https://brand.creatoros.xyz/"
          target="_blank"
          rel="noopener noreferrer"
          variant="text"
          size="md"
          className="inline-flex font-semibold"
        >
          Sign in
        </Button>
        <Button as={Link} href="/contact" variant="filled">
          {desktopCtaLabel}
        </Button>
      </div>
    </header>
  );
};
